import { Component, Host, Prop, h, State, Element } from '@stencil/core';
import { uuidv4 } from '@/globals/helpers';

/**
 * The `pn-textarea` is built with a native `textarea`.
 * This means you can use native events to listen to the input.
 *
 * @nativeInput Use the `input` event to listen to content being modified by the user.
 */
@Component({
  tag: 'pn-textarea',
  styleUrl: 'pn-textarea.scss',
})
export class PnTextarea {
  id: string = `pn-textarea-${uuidv4()}`;
  idHelper: string = `${this.id}-text`;

  @Element() hostElement: HTMLElement;

  /** The textarea content */
  @State() content: number;

  /** The label describing the textarea. */
  @Prop() label!: string;
  /** The textarea content, do not provide slotted content. */
  @Prop() value: string = '';
  /** Helper text that will appear underneath the textarea. Will be overwritten if you set an `error` string */
  @Prop() helpertext?: string;

  /** A unique ID connecting the element to the label. @category HTML Textarea */
  @Prop() textareaid?: string = this.id;
  /** HTML name. @category HTML Textarea */
  @Prop() name?: string;
  /** Set the associated form. @category HTML Textarea*/
  @Prop() form?: string;
  /** Set the row count for the textarea. @category HTML Textarea */
  @Prop() rows?: number = 2;
  /** Set the col count for the textarea. @category HTML Textarea */
  @Prop() cols?: number = 20;
  /** Set the wrap control. @category HTML Textarea */
  @Prop() wrap?: string = 'soft';
  /** The maximum number of characters the user should be able to add, also adds a visible counter. @category HTML Textarea */
  @Prop() maxlength?: number;
  /** Allow the browser to autocomplete the textarea. @category HTML Textarea */
  @Prop() autocomplete?: string = 'off';
  /** Allow the browser to spellcheck the textarea. @category HTML Textarea */
  @Prop() spellcheck: boolean = false;
  /** Placeholder for the textarea. @category HTML Textarea */
  @Prop() placeholder?: string;
  /** Allow the user to resize the textarea vertically. Handle width in your own layout. @category HTML Textarea */
  @Prop() resize?: boolean = false;
  /** Make the textarea required. @category HTML Textarea */
  @Prop() required?: boolean = false;

  /** Set the textarea as `valid`, will make the Label and focus rings green. @category State */
  @Prop() valid?: boolean = false;
  /** Set the textarea as `invalid`, will make the Label and focus rings red. @category State */
  @Prop() invalid?: boolean = false;
  /** Same as `invalid`, but will display the string as an error message under the textarea. @category State */
  @Prop() error?: string;
  /** Disable the textarea. @category State */
  @Prop() disabled?: boolean = false;
  /** Make the textarea readonly. @category State */
  @Prop() readonly?: boolean = false;

  componentWillLoad() {
    if (this.textareaid !== this.id) {
      this.idHelper = `${this.textareaid}-helper`;
    }
  }

  setVal(event: Event) {
    const value = (event.target as HTMLTextAreaElement).value;
    this.value = value;
  }

  hasError(): boolean {
    return this.invalid || !!this.error;
  }

  hasMessage(): boolean {
    return !!(this.helpertext?.length || this.error?.length);
  }

  render() {
    return (
      <Host>
        <div class="pn-textarea" data-valid={this.valid} data-error={this.hasError()} data-resize={this.resize}>
          <label class="pn-textarea-label" htmlFor={this.textareaid}>
            <span>{this.label}</span>
            {this.maxlength >= 1 && <span>{`${this.value?.length || 0}/${this.maxlength}`}</span>}
          </label>
          <textarea
            class="pn-textarea-element"
            id={this.textareaid}
            name={this.name}
            rows={this.rows}
            cols={this.cols}
            wrap={this.wrap}
            autocomplete={this.autocomplete}
            spellcheck={this.spellcheck}
            placeholder={this.placeholder}
            maxlength={this.maxlength}
            required={this.required}
            disabled={this.disabled}
            readonly={this.readonly}
            aria-describedby={this.hasMessage() ? this.idHelper : null}
            aria-invalid={this.hasError().toString()}
            onInput={e => this.setVal(e)}
            value={this.value}
          />

          {this.hasMessage() && (
            <p id={this.idHelper} class="pn-textarea-text" role={this.error?.length ? 'alert' : null}>
              <span>{this.error || this.helpertext}</span>
            </p>
          )}
        </div>
      </Host>
    );
  }
}
